import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SaveIcon from '@material-ui/icons/Save';
import Axios from 'axios';
import { baseUrl } from '../../../util/host';
import { useDispatch, useSelector } from 'react-redux';
import { loadCentralWarehouses, loadCountries, loadWhCountryMappings, updateWhCountryMappings } from '../../../store/country/countryActions';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    maxWidth: 800,
    margin: '40px auto',
    backgroundColor: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 500,
    margin: '0 auto',
  },
  formSection: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(4),
  },
  selectsContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  formControl: {
    flex: 1,
    minWidth: 200,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
    },
  },
  buttonWrapper: {
    position: 'relative',
    marginTop: theme.spacing(4),
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  actionButton: {
    padding: theme.spacing(1.5, 4),
    borderRadius: 30,
    minWidth: 180,
    boxShadow: '0 3px 5px 2px rgba(63, 81, 181, 0.15)',
    '&:hover': {
      boxShadow: '0 4px 8px 2px rgba(63, 81, 181, 0.2)',
    },
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const LastMileSLA = () => {
  const classes = useStyles();
  // const [country, setCountry] = useState(['SA']);
  const [warehouse, setWarehouse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();
  const centralWarehousesSelector = useSelector((state) => state.country.centralWarehouses);
  const countriesSelector = useSelector((state) => state.country.countries);
  const whCountriesMappingsSelector = useSelector((state) => state.country.whCountriesMappings);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(loadCentralWarehouses()),
      dispatch(loadCountries()),
      dispatch(loadWhCountryMappings())
    ]).then(() => {
      setLoading(false);
    }).catch((err) => {
      console.log(err);
      setLoading(false);
    });
  }, [dispatch]);

  useEffect(() => {
    const list = centralWarehousesSelector;
    if (!list || list.length === 0) return;
    const firstId = list[0].warehouse_id;
    setWarehouse((prev) => {
      if (!prev) return firstId;
      const stillExists = list.some((w) => w.warehouse_id === prev);
      return stillExists ? prev : firstId;
    });
  }, [centralWarehousesSelector]);

  const handleCountryChange = (event) => {
    const selectedCountries = event.target.value;
    dispatch(updateWhCountryMappings(warehouse, selectedCountries));
  };

  const handleWarehouseChange = (event) => {
    setWarehouse(event.target.value);
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      await Axios.post(
        `${baseUrl('REACT_APP_API_URL')}/api/address/warehouse-country-mappings`,
        {
          warehouse: warehouse,
          countries: whCountriesMappingsSelector.get(warehouse) || []
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      setSuccess(true);
    } catch (err) {
      const errorMessage = err.response?.data?.statusMsg || err.message || 'Failed to save configuration. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Last Mile SLA Configuration
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          Configure destination countries and central warehouse for Last Mile SLA settings.
        </Typography>
      </div>

      <div className={classes.formSection}>
        <div className={classes.selectsContainer}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="warehouse-select-label">Central Warehouse</InputLabel>
            <Select
              labelId="warehouse-select-label"
              id="warehouse-select"
              value={warehouse}
              onChange={handleWarehouseChange}
              label="Central Warehouse"
              displayEmpty
              renderValue={(selected) =>
                selected
                  ? (centralWarehousesSelector.find((w) => w.warehouse_id === selected)?.warehouse_name ||
                      selected)
                  : ''
              }
            >
              {
                centralWarehousesSelector.map((w) => (
                  <MenuItem key={w.warehouse_id} value={w.warehouse_id}>
                    {w.warehouse_name || w.warehouse_id || 'Unknown Warehouse'}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel id="country-select-label">Destination Countries</InputLabel>
            <Select
              labelId="country-select-label"
              id="country-select"
              multiple
              value={whCountriesMappingsSelector.get(warehouse) || []}
              onChange={handleCountryChange}
              label="Destination Countries"
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={countriesSelector.find(c => c.code === value)?.name} size="small" />
                  ))}
                </div>
              )}
            >
              {countriesSelector.map((country) => (
                <MenuItem value={country.code}>{country.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className={classes.buttonWrapper}>
        <Button
          variant="contained"
          color="primary"
          disabled={!warehouse || loading}
          onClick={handleSave}
          startIcon={<SaveIcon />}
          className={classes.actionButton}
        >
          Save Configuration
        </Button>
        {loading && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
      </div>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
      >
        <Alert onClose={() => setSuccess(false)} severity="success">
          Configuration saved successfully!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default LastMileSLA; 